export const SOLAR_RAMP_SUITABILITY_SYSTEMS = ['Double', 'Triple', 'Quattro'] as const

export type SolarRampSuitabilitySystem = (typeof SOLAR_RAMP_SUITABILITY_SYSTEMS)[number]

export type VanDerValkSolarRampVendorSystemType = 'VALK_DOUBLE' | 'VALK_TRIPLE' | 'VALK_QUATTRO'

export type VanDerValkSolarRampFoundationType = 'MASS_BLOCKS' | 'TILE_CARRIERS'

export type VanDerValkSolarRampMiddleZoneDeflector = 'OPEN_SYSTEM' | 'CLOSED_SYSTEM'

export type VanDerValkSolarRampFixation = 'BALLASTING' | 'ANCHORING'

export type VanDerValkSolarRampAnchorType = 'VALK_SOLAR_FIX' | 'OTHER'

export const SOLAR_RAMP_MIDDLE_ZONE_DEFLECTOR_OPTIONS: readonly VanDerValkSolarRampMiddleZoneDeflector[] = [
  'OPEN_SYSTEM',
  'CLOSED_SYSTEM',
]

export type SolarRampProfile = {
  systemType: VanDerValkSolarRampVendorSystemType
  pitchMm: number
  foundationTypes: readonly VanDerValkSolarRampFoundationType[]
}

export const SOLAR_RAMP_PROFILES: Record<SolarRampSuitabilitySystem, SolarRampProfile> = {
  Double: {
    systemType: 'VALK_DOUBLE',
    pitchMm: 2488,
    foundationTypes: ['MASS_BLOCKS', 'TILE_CARRIERS'],
  },
  Triple: {
    systemType: 'VALK_TRIPLE',
    pitchMm: 3535,
    foundationTypes: ['MASS_BLOCKS', 'TILE_CARRIERS'],
  },
  Quattro: {
    systemType: 'VALK_QUATTRO',
    pitchMm: 4692,
    foundationTypes: ['MASS_BLOCKS'],
  },
}

export function isVanDerValkSolarRampSystem(system: string | null | undefined): system is SolarRampSuitabilitySystem {
  if (system == null || typeof system !== 'string') {
    return false
  }
  return (SOLAR_RAMP_SUITABILITY_SYSTEMS as readonly string[]).includes(system)
}

export function getSolarRampProfile(system: string | null | undefined): SolarRampProfile | null {
  if (!isVanDerValkSolarRampSystem(system)) {
    return null
  }
  return SOLAR_RAMP_PROFILES[system]
}

export function resolveSolarRampVendorSystemType(
  system: string | null | undefined
): VanDerValkSolarRampVendorSystemType | null {
  return getSolarRampProfile(system)?.systemType ?? null
}

export function resolveSolarRampPitchMm(system: string | null | undefined): number | null {
  return getSolarRampProfile(system)?.pitchMm ?? null
}

export function isSolarRampFoundationTypeAllowed(
  system: string | null | undefined,
  foundationType: string | null | undefined
): foundationType is VanDerValkSolarRampFoundationType {
  const profile = getSolarRampProfile(system)
  if (!profile || foundationType == null) {
    return false
  }
  return (profile.foundationTypes as readonly string[]).includes(foundationType)
}

export const DEFAULT_SOLAR_RAMP_FOUNDATION_TYPE: VanDerValkSolarRampFoundationType = 'MASS_BLOCKS'
export const DEFAULT_SOLAR_RAMP_FIXATION: VanDerValkSolarRampFixation = 'BALLASTING'
export const DEFAULT_SOLAR_RAMP_MIDDLE_ZONE_DEFLECTOR: VanDerValkSolarRampMiddleZoneDeflector = 'OPEN_SYSTEM'
